/**
 * BaseTile - Shared base component for all CCM tiles
 *
 * Features full-bleed background images with dark overlay for text readability.
 * Provides common functionality:
 * - Click handling with hover effects
 * - Relevance detail on hover
 * - Admin mode controls (remove, reorder)
 * - Entity type indicator
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Fade,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import { ENTITY_TYPE_CONFIG } from '../demoResources';

// Entity type icons
const ENTITY_ICONS = {
  person: PersonIcon,
  organization: BusinessIcon,
  program: SchoolIcon,
  community: GroupsIcon,
};

// eslint-disable-next-line max-lines-per-function
function BaseTile({
  resource,
  sectionColor,
  onClick,
  adminMode,
  onRemove,
  onMoveLeft,
  onMoveRight,
  isFirst,
  isLast,
  width = 280,
  height = 220,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Responsive dimensions - larger tiles on mobile for better touch targets
  const responsiveWidth = isMobile ? 'calc(100vw - 48px)' : width;
  const responsiveMinWidth = isMobile ? 300 : width;
  const responsiveMaxWidth = isMobile ? 400 : width;
  const responsiveHeight = isMobile ? 260 : height;

  const EntityIcon = ENTITY_ICONS[resource.entityType] || PersonIcon;
  const entityConfig = ENTITY_TYPE_CONFIG[resource.entityType] || ENTITY_TYPE_CONFIG.person;

  return (
    <Paper
      elevation={isHovered ? 8 : 2}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      sx={{
        width: responsiveWidth,
        minWidth: responsiveMinWidth,
        maxWidth: responsiveMaxWidth,
        height: responsiveHeight,
        flexShrink: 0,
        cursor: 'pointer',
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-6px) scale(1.02)' : 'translateY(0) scale(1)',
        bgcolor: '#1a1a2e', // Fallback dark background
      }}
    >
      {/* Background Image Layer */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          backgroundImage: resource.avatar ? `url(${resource.avatar})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'transform 0.3s ease',
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
        }}
      />

      {/* Dark Overlay for Text Readability */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          background: `linear-gradient(
            180deg,
            rgba(0,0,0,0.2) 0%,
            rgba(0,0,0,0.4) 50%,
            rgba(0,0,0,0.85) 100%
          )`,
        }}
      />

      {/* Admin Controls Overlay */}
      {adminMode && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 20,
            display: 'flex',
            gap: 0.5,
            p: 0.5,
            bgcolor: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(8px)',
            borderBottomLeftRadius: 12,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            onClick={onMoveLeft}
            disabled={isFirst}
            sx={{ p: 0.5, color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onMoveRight}
            disabled={isLast}
            sx={{ p: 0.5, color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{ p: 0.5, color: 'error.main' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Entity Type Badge - top left */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 10,
        }}
      >
        <Chip
          icon={<EntityIcon sx={{ fontSize: 14, color: 'white !important' }} />}
          label={entityConfig.label}
          size="small"
          sx={{
            height: 24,
            bgcolor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.2)',
            '& .MuiChip-label': {
              px: 0.75,
              fontSize: '0.7rem',
              fontWeight: 500,
            },
          }}
        />
      </Box>

      {/* Content container at bottom */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          p: 2,
        }}
      >
        {/* Name */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            color: '#ffffff !important',
            textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,1)',
            mb: 0.5,
          }}
        >
          {resource.name}
        </Typography>

        {/* Title / Organization */}
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: 'rgba(255,255,255,0.95) !important',
            fontSize: '0.8rem',
            mb: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textShadow: '0 1px 4px rgba(0,0,0,0.9)',
          }}
        >
          {resource.title}
          {resource.organization && ` • ${resource.organization}`}
        </Typography>

        {/* Relevance tag */}
        <Chip
          label={resource.relevanceTag}
          size="small"
          sx={{
            bgcolor: sectionColor,
            color: '#ffffff !important',
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            '& .MuiChip-label': {
              px: 1.5,
              color: '#ffffff !important',
            },
          }}
        />
      </Box>

      {/* Hover overlay with relevance detail */}
      <Fade in={isHovered && !adminMode}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 15,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2.5,
            bgcolor: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography
            variant="body2"
            component="div"
            sx={{
              color: '#ffffff !important',
              fontSize: '0.85rem',
              lineHeight: 1.6,
              textAlign: 'center',
            }}
          >
            {resource.relevanceDetail}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            sx={{
              color: `${sectionColor} !important`,
              fontWeight: 600,
              mt: 1.5,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Click to view profile
          </Typography>
        </Box>
      </Fade>
    </Paper>
  );
}

export default BaseTile;
